import { useMemo, useRef } from 'react';

import { LegalClientInnerItemEntity } from 'api/types/entity/legal/legalClientInnerItemEntity';
import { formatToDate } from 'helpers';
import { useFormContext } from 'libs/form/useFormContext';
import { useTranslation } from 'libs/i18n';
import { IconButton } from 'libs/ui';
import { useDialog } from 'libs/ui/Dialog/useDialog';
import { Icon } from 'libs/ui/Icon';
import { Section } from 'modules/client/common/components';
import {
  CountryRiskField,
  CountryRiskRole,
  getMainAddressCountryRisk,
} from 'modules/client/common/feature';

import { HeadAddDialog } from './HeadAddDialog';
import { HeadArchiveDialog } from './HeadArchiveDialog';
import { HeadDeleteDialog } from './HeadDeleteDialog';
import { HeadsInfoCard, HeadItem, HeadType } from './HeadsInfoCard';

export interface CompanyHeadsSectionProps {
  heads: LegalClientInnerItemEntity[];
  legalHeads: LegalClientInnerItemEntity[];
  onClickHead?: (id: string) => void;
  onClickAddHead?: () => void;
  onClickAddLegalHead?: () => void;
  onClickLegalHead?: (id: string) => void;
  onDeleteHead?: (id: string, reason: string) => Promise<void>;
  onDeleteLegalHead?: (id: string, reason: string) => Promise<void>;
  onArchiveHead?: (id: string, reason: string) => Promise<void>;
  onArchiveLegalHead?: (id: string, reason: string) => Promise<void>;
  onDearchiveHead?: (id: string, reason: string) => Promise<void>;
  onDearchiveLegalHead?: (id: string, reason: string) => Promise<void>;
}

export const CompanyHeadsSection = ({
  heads,
  legalHeads,
  onClickHead,
  onClickAddHead,
  onClickLegalHead,
  onClickAddLegalHead,
  onDeleteHead,
  onDeleteLegalHead,
  onArchiveHead,
  onArchiveLegalHead,
  onDearchiveHead,
  onDearchiveLegalHead,
}: CompanyHeadsSectionProps) => {
  const { t } = useTranslation();

  const { readOnly } = useFormContext();

  const { open: openAddHeadDialog, ...addHeadDialogProps } = useDialog();
  const { open: openArchiveDialog, ...archiveDialogProps } = useDialog();

  const activeHeadItem = useRef<HeadItem>();

  const {
    isOpen: isOpenDeleteDialog,
    onClose: onCloseDeleteDialog,
    open: openDeleteDialogReason,
  } = useDialog();

  const items: HeadItem[] = useMemo(() => {
    const personHeads: HeadItem[] = heads.map((head) => {
      const countryRisk = getMainAddressCountryRisk(
        head.residentialCountry,
        head.registrationCountry
      );

      return {
        id: head.id,
        name: head.name,
        country: {
          [HeadType.person]: countryRisk.country?.name,
        },
        countryRisk: { ...countryRisk, role: CountryRiskRole.LegalClientHead },
        type: HeadType.person,
        createdAt: head.createdAt,
        archived: head.archived,
        position: head.positionInCompany?.position,
        expirationDate: head.positionInCompany?.expirationDate
          ? formatToDate(head.positionInCompany.expirationDate)
          : undefined,
      };
    });

    const legalHeadsItems: HeadItem[] = legalHeads.map((legalHead) => ({
      id: legalHead.id,
      name: legalHead.name,
      country: {
        [HeadType.legal]: legalHead.registrationCountry?.name,
      },
      countryRisk: {
        country: legalHead.registrationCountry,
        field: CountryRiskField.CompanyLegalAddress,
        role: CountryRiskRole.LegalClientLegalHead,
      },
      type: HeadType.legal,
      createdAt: legalHead.createdAt,
      archived: legalHead.archived,
      position: legalHead.positionInCompany?.position,
      expirationDate: legalHead.positionInCompany?.expirationDate
        ? formatToDate(legalHead.positionInCompany.expirationDate)
        : undefined,
    }));

    const allHeadItems = personHeads.concat(legalHeadsItems);

    allHeadItems.sort((item, nextItem) =>
      item.archived === nextItem.archived
        ? nextItem.createdAt.getTime() - item.createdAt.getTime()
        : item.archived
        ? 1
        : -1
    );

    return allHeadItems;
  }, [heads, legalHeads]);

  const onCellClick = (item: HeadItem) => {
    if (item.type === HeadType.person) {
      onClickHead?.(item.id);
      return;
    }
    onClickLegalHead?.(item.id);
  };

  const onClickPerson = () => {
    addHeadDialogProps.onClose();
    onClickAddHead?.();
  };

  const onClickLegal = () => {
    addHeadDialogProps.onClose();
    onClickAddLegalHead?.();
  };

  const onClickDelete =
    onDearchiveHead && onDeleteLegalHead
      ? (row: HeadItem) => {
          activeHeadItem.current = row;
          openDeleteDialogReason();
        }
      : undefined;

  const onClickArchive =
    onArchiveHead && onArchiveLegalHead
      ? (row: HeadItem) => {
          activeHeadItem.current = row;
          openArchiveDialog();
        }
      : undefined;

  return (
    <Section
      datax="companyHeadsSection"
      headerRight={
        !readOnly &&
        onClickAddHead &&
        onClickAddLegalHead && (
          <IconButton
            icon={<Icon.Plus size={16} />}
            mode="outlined"
            sizeVariant="xs"
            onClick={openAddHeadDialog}
          />
        )
      }
      title={t('client.legal.companyHeads.title')}
    >
      <HeadsInfoCard
        editable={!readOnly}
        items={items}
        onCellClick={onCellClick}
        onClickArchive={onClickArchive}
        onClickDelete={onClickDelete}
      />

      {onClickAddHead && onClickAddLegalHead && (
        <HeadAddDialog
          {...addHeadDialogProps}
          onClickLegal={onClickLegal}
          onClickPerson={onClickPerson}
        />
      )}

      <HeadDeleteDialog
        activeHeadItem={activeHeadItem.current}
        isOpen={isOpenDeleteDialog}
        onClose={onCloseDeleteDialog}
        onDeleteHead={onDeleteHead}
        onDeleteLegalHead={onDeleteLegalHead}
      />

      <HeadArchiveDialog
        activeHeadItem={activeHeadItem.current}
        onArchiveHead={onArchiveHead}
        onArchiveLegalHead={onArchiveLegalHead}
        onDearchiveHead={onDearchiveHead}
        onDearchiveLegalHead={onDearchiveLegalHead}
        {...archiveDialogProps}
      />
    </Section>
  );
};
